// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import moment from "moment";
import { ColumnsType } from "antd/es/table";
import { ReactSVG } from "react-svg";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// Local Imports
import CustomTable from "../../../../components/custom-table/custom-table";
import { Notification } from "../../../../models/notification.model";
import CustomModal from "../../../../components/custom-modal/custom-modal";
import ViewNotification from "../view-notification/view-notification.component";
import CardsService from "../../cards.service";
import { Checkbox, DatePicker, Drawer } from "antd";
import UsersList from "./components/users-list";
import CustomSearch from "src/components/custom-search/custom-search";

let searchTimer = null;

const NotificationCenter: React.FC = (): ReactElement => {
  const cardsService = new CardsService();
  const loadingState = useSelector((state: any) => state?.loading);
  const navigate = useNavigate();
  const [notificationlist, setNotificationList] = useState<Notification[]>([]);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] =
    useState<Notification>();
  const [pagination, setPagination] = useState<{
    pages: number;
    currentPage: number;
    total: number;
  }>({ pages: 1, currentPage: 1, total: 0 });
  const [openUsersListDrawer, setOpenUsersListDrawer] =
    useState<boolean>(false);
  const [searchWord, setSearchWord] = useState(null);
  const [openAdvancedFilterModal, setOpenAdvancedFilterModal] =
    useState<boolean>(false);
  const [fromDate, setFromDate] = useState<string>(null);
  const [toDate, setToDate] = useState<string>(null);
  const [hasImage, setHasImage] = useState<boolean>(false);
  const [hasLink, setHasLink] = useState<boolean>(false);

  useEffect(() => {
    if (openAdvancedFilterModal) {
      return;
    }
    fetchNotificationListData();
  }, [searchWord, fromDate, toDate, hasImage, hasLink]);

  const onSearch = (e) => {
    if (openAdvancedFilterModal) {
      return;
    }
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setSearchWord(e.target.value);
    }, 1000);
  };

  const fetchNotificationListData = async (page = 1): Promise<void> => {
    let query = `?page=${page}&pageSize=20`;
    if (searchWord) {
      query += `&search=${searchWord}`;
    }
    if (fromDate) {
      query += `&fromDate=${fromDate}`;
    }
    if (toDate) {
      query += `&toDate=${toDate}`;
    }
    if (hasImage) {
      query += `&hasImage=true`;
    }
    if (hasLink) {
      query += `&hasLink=true`;
    }

    const data:
      | {
          notifications: Notification[];
          pagination: { pages: number; currentPage: number; total: number };
        }
      | undefined = await cardsService.getNotificationListData(query);

    if (data !== undefined) {
      setNotificationList(data.notifications);
      setPagination(data.pagination);
    }
  };

  const columns: ColumnsType<Notification> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_, __, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Message",
      dataIndex: "title",
      key: "title",
      sorter: (a: Notification, b: Notification) =>
        String(a?.title).localeCompare(String(b?.title)),
      fixed: "left",
      render: (text: string, record: Notification) => (
        <div className="flex flex-col gap-1">
          <span className="text-sky-900 text-xs font-semibold font-['Montserrat']">
            {text}
          </span>
          <span className="text-neutral-700 text-xs font-medium font-['Montserrat']">
            {record?.body}
          </span>
        </div>
      ),
    },
    {
      title: "Users",
      dataIndex: "user",
      key: "user",
      render: (_, record: any) =>
        record?.type === "GENERIC" ? (
          <div
            onClick={() => {
              setSelectedNotification(record);
              setOpenUsersListDrawer(true);
            }}
            className="cursor-pointer justify-center text-sky-900 text-xs font-medium font-['Montserrat'] underline"
          >
            All users
          </div>
        ) : (
          <div
            onClick={() => {
              setSelectedNotification(record);
              setOpenUsersListDrawer(true);
            }}
            className="cursor-pointer justify-center text-sky-900 text-xs font-medium font-['Montserrat'] underline"
          >
            Selected users
          </div>
        ),
    },
    {
      title: "Sent at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a: Notification, b: Notification) =>
        moment(a?.createdAt).isBefore(moment(b?.createdAt)) ? -1 : 1,
      render: (text: string) => (
        <span className=" text-gray-500">
          {moment(text).format("DD MMM, YYYY hh:mm A")}
        </span>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <ReactSVG
            src="/images/view.svg"
            onClick={() => {
              setSelectedNotification(record);
              setOpenViewModal(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className=" mt-5 border-t-[1px] border-gray-400 rounded-lg">
      <div
        className="fixed z-0 cursor-pointer right-5 md:right-10 top-[170px] md:top-[110px] h-9 px-2.5 py-1 bg-[#002f6c] rounded-[100px] border border-black justify-center items-center gap-2 inline-flex"
        onClick={() => navigate("/cards/add-notification")}
      >
        <img
          src="/images/white-add-square.svg"
          alt="send message"
          className="w-5 h-5"
        />
        <div className=" text-white text-[13px] font-medium">
          Send a new message
        </div>
      </div>
      <div className="flex items-center justify-between rounded-lg bg-white py-[10px] px-5">
        <p className=" text-black-300 text-base font-bold">
          {!loadingState ? notificationlist?.length : ""} messages
        </p>
        <div className="flex items-center gap-1">
          <CustomSearch onChange={(event) => onSearch(event)} />
          <img
            src="/images/filter-icon.svg"
            alt="filter"
            className="cursor-pointer"
            onClick={() => setOpenAdvancedFilterModal(true)}
          />
        </div>
      </div>
      {notificationlist?.length ? (
        <CustomTable
          columns={columns}
          data={notificationlist}
          showPagination={true}
          pageSize={20}
          totalCount={pagination?.total}
          page={pagination?.currentPage}
          onPageChange={(page) => {
            fetchNotificationListData(page);
          }}
        />
      ) : !loadingState ? (
        <div className="flex flex-col gap-3 w-full items-center justify-center mt-9">
          <img
            className="w-[40px] h-[40px]"
            src="/images/no-items.svg"
            alt="no data kamco"
          />
          <p className="text-lg text-gray-500 font-medium">
            There are no items in this list
          </p>
        </div>
      ) : (
        ""
      )}

      <CustomModal
        isVisible={openViewModal}
        centered
        width={600}
        onClose={() => {
          setOpenViewModal(false);
        }}
        className="edit-modal"
        showClose={false}
      >
        <div className="flex flex-col items-center justify-center">
          <ViewNotification
            selectedNotification={selectedNotification}
            onCancel={() => {
              setOpenViewModal(false);
            }}
          />
        </div>
      </CustomModal>
      <Drawer
        title="Basic Drawer"
        open={openUsersListDrawer}
        destroyOnClose={true}
        maskClosable={true}
        onClose={() => setOpenUsersListDrawer(false)}
        width={800}
        className="change-account-drawer"
      >
        <UsersList notificationId={selectedNotification?.id} />
      </Drawer>

      <CustomModal
        isVisible={openAdvancedFilterModal}
        centered
        width={500}
        onClose={() => {
          setOpenAdvancedFilterModal(false);
        }}
        className="edit-modal"
        showClose={false}
      >
        <div className="flex flex-col gap-5 px-5">
          <div className="self-stretch inline-flex justify-between items-center">
            <span
              className="cursor-pointer text-gray-500 text-xl"
              onClick={() => setOpenAdvancedFilterModal(false)}
            >
              X
            </span>
            <div className="text-center justify-center text-neutral-800 text-lg font-bold font-['Montserrat']">
              Advanced filter
            </div>
            <div
              className="cursor-pointer text-center justify-center text-neutral-800 text-sm font-semibold font-['Montserrat'] underline"
              onClick={async () => {
                setSearchWord(null);
                setFromDate(null);
                setToDate(null);
                setHasImage(false);
                setHasLink(false);

                const data = await cardsService.getNotificationListData(
                  "?page=1&pageSize=20"
                );
                if (data !== undefined) {
                  setNotificationList(data.notifications);
                  setPagination(data.pagination);
                  setOpenAdvancedFilterModal(false);
                }
              }}
            >
              Reset
            </div>
          </div>
          <div className="self-stretch inline-flex flex-col justify-start items-end gap-8">
            <div className="self-stretch flex flex-col justify-start items-start gap-8">
              <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-start items-start gap-2">
                  <div className="flex-1 flex justify-start items-start gap-2">
                    <div className="flex-1 self-stretch justify-center text-neutral-600 text-xs font-medium font-['Montserrat']">
                      Complete the fields below and apply the filter{" "}
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Keywords"
                  value={searchWord || ""}
                  onChange={(e) => setSearchWord(e.target.value)}
                  className="w-full p-3 rounded-lg border border-gray-400"
                />
                <div className="self-stretch inline-flex justify-start items-center gap-2">
                  <DatePicker
                    placeholder="From Date"
                    className="w-full p-3 rounded-lg border border-gray-400/20"
                    onChange={(date) => setFromDate(date?.toISOString())}
                  />
                  <DatePicker
                    placeholder="To Date"
                    className="w-full p-3 rounded-lg border border-gray-400/20"
                    onChange={(date) => setToDate(date?.toISOString())}
                  />
                </div>
                <div className="self-stretch inline-flex justify-start items-start gap-8">
                  <div className="flex justify-start items-center gap-2">
                    <Checkbox
                      checked={hasImage}
                      onChange={(e) => setHasImage(e.target.checked)}
                    />
                    <div className="opacity-80 text-center justify-start text-zinc-800 text-base font-medium font-['Montserrat']">
                      Has image
                    </div>
                  </div>
                  <div className="opacity-80 flex justify-start items-center gap-2">
                    <Checkbox
                      checked={hasLink}
                      onChange={(e) => setHasLink(e.target.checked)}
                    />
                    <div className="text-center justify-start text-zinc-800 text-base font-medium font-['Montserrat']">
                      Has link
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="self-stretch inline-flex justify-end items-start gap-3">
              <button
                onClick={() => setOpenAdvancedFilterModal(false)}
                className="flex-1 p-4 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-blue-500  flex justify-center items-center gap-2.5"
              >
                <div className="text-center justify-start text-blue-500 text-sm font-bold font-['Montserrat'] leading-none">
                  Cancel
                </div>
              </button>
              <button
                onClick={() => {
                  fetchNotificationListData(1);
                }}
                disabled={
                  !searchWord && !fromDate && !toDate && !hasImage && !hasLink
                }
                className="disabled:opacity-50 flex-1 p-4 bg-blue-500 rounded-lg flex justify-center items-center gap-2.5"
              >
                <div className="text-center justify-start text-white text-sm font-bold font-['Montserrat'] leading-none">
                  Apply fitler
                </div>
              </button>
            </div>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default NotificationCenter;
